import { useState } from 'react';

import { AttentionQueueView } from './AttentionQueue';
import { RosterTable } from './RosterTable';
import { TopicsTable } from './TopicsTable';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { AttentionRow } from '@/lib/api/attention';

/**
 * Three answers to three questions, behind one segmented control.
 *
 *   Needs attention — who do I go and talk to?   (the default)
 *   All students    — how is everyone?
 *   Topics          — what did the class not get?
 *
 * A segmented control rather than three more batch tabs: the batch header
 * already carries eight, and these three are one another's context — a trainer
 * flicks between them while thinking about the same question. Making them tabs
 * would scatter that across the page and push the whole row off a laptop.
 */
type View = 'queue' | 'roster' | 'topics';

const VIEWS: { key: View; label: string }[] = [
  { key: 'queue', label: 'Needs attention' },
  { key: 'roster', label: 'All students' },
  { key: 'topics', label: 'Topics' },
];

export function AttentionPanel({
  batchId,
  onRaise,
}: {
  batchId: string;
  onRaise?: (row: AttentionRow) => void;
}) {
  const [view, setView] = useState<View>('queue');

  return (
    <Tabs value={view} onValueChange={(v) => setView(v as View)} className="space-y-4">
      <TabsList>
        {VIEWS.map((v) => (
          <TabsTrigger key={v.key} value={v.key} className="text-xs">
            {v.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="queue">
        <AttentionQueueView batchId={batchId} onRaise={onRaise} />
      </TabsContent>
      <TabsContent value="roster">
        <RosterTable batchId={batchId} />
      </TabsContent>
      <TabsContent value="topics">
        <TopicsTable batchId={batchId} />
      </TabsContent>
    </Tabs>
  );
}
